import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import {
  LayoutList,
  Plus,
  Save,
  Trash2,
  Eye,
  EyeOff,
  Image as ImageIcon,
  RotateCcw,
  X,
} from "lucide-react";

const PANEL_BG = "#111a26";
const PANEL_BORDER = "#1f2a3a";
const ACCENT = "#19a55c";

export default function Categories() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("primary"); // primary | secondary
  const [saving, setSaving] = useState({});
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/categories");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const updateField = (id, patch) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...patch, _dirty: true } : it))
    );
  };

  const saveRow = async (it) => {
    setSaving((s) => ({ ...s, [it.id]: true }));
    try {
      const { data } = await api.patch(`/categories/${it.id}`, {
        label: it.label,
        icon: it.icon || "",
        order: Number(it.order || 0),
        to: it.to || "/",
        enabled: !!it.enabled,
        kind: it.kind,
      });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...data, _dirty: false } : x)));
      toast.success(`Saved "${data.label}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [it.id]: false }));
    }
  };

  const toggleEnabled = async (it) => {
    try {
      const { data } = await api.patch(`/categories/${it.id}`, { enabled: !it.enabled });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...data } : x)));
      toast.success(data.enabled ? `"${data.label}" is now visible` : `"${data.label}" hidden`);
    } catch (e) {
      toast.error("Toggle failed");
    }
  };

  const removeRow = async (it) => {
    if (!window.confirm(`Delete category "${it.label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/categories/${it.id}`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      toast.success(`Deleted "${it.label}"`);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Wipe ALL custom categories and restore defaults? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/categories/reset");
      setItems(data.items);
      toast.success(`Reset — ${data.count} default categories restored`);
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  if (!items) {
    return (
      <div className="space-y-6" data-testid="categories-loading">
        <PageHeader icon={LayoutList} title="Site Categories" description="Loading…" />
      </div>
    );
  }

  const filtered = items.filter((it) => it.kind === tab).sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-6" data-testid="categories-page">
      <PageHeader
        icon={LayoutList}
        title="Site Categories"
        description="Control the public site's navigation — rename, reorder, hide, or add categories. Changes appear instantly on the site."
      />

      {/* Tabs + actions */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: PANEL_BG, border: `1px solid ${PANEL_BORDER}` }}>
          {[
            { id: "primary", label: "Primary — Main Sidebar" },
            { id: "secondary", label: "Secondary — Quick Links" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`categories-tab-${t.id}`}
              className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${
                tab === t.id
                  ? "text-white"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
              style={tab === t.id ? { background: ACCENT } : {}}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={resetAll}
            data-testid="categories-reset-btn"
            className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-zinc-400 hover:text-white border border-[#27272A] hover:border-red-500/40 rounded-md transition-colors"
          >
            <RotateCcw size={14} /> Reset to Defaults
          </button>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid="categories-add-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors"
            style={{ background: ACCENT }}
          >
            <Plus size={16} /> Add Category
          </button>
        </div>
      </div>

      {/* Table */}
      <div
        className="rounded-lg overflow-hidden"
        style={{ background: PANEL_BG, border: `1px solid ${PANEL_BORDER}` }}
      >
        <div className="grid grid-cols-[60px_80px_1.5fr_1fr_2fr_130px_180px] gap-3 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 border-b border-[#1f2a3a]">
          <div>Order</div>
          <div>Icon</div>
          <div>Label</div>
          <div>Slug</div>
          <div>Route (URL path)</div>
          <div className="text-center">Visible</div>
          <div className="text-center">Actions</div>
        </div>

        {filtered.length === 0 && (
          <div className="px-4 py-16 text-center text-zinc-500 text-sm">
            No {tab} categories yet. Click <span className="text-white font-semibold">"Add Category"</span> to create one.
          </div>
        )}

        {filtered.map((it) => (
          <div
            key={it.id}
            data-testid={`category-row-${it.slug}`}
            className={`grid grid-cols-[60px_80px_1.5fr_1fr_2fr_130px_180px] gap-3 px-4 py-3 items-center border-b border-[#1f2a3a] last:border-b-0 ${
              it.enabled ? "" : "opacity-55"
            }`}
          >
            <input
              type="number"
              value={it.order || 0}
              onChange={(e) => updateField(it.id, { order: Number(e.target.value) })}
              data-testid={`category-order-${it.slug}`}
              className="w-full h-8 px-2 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm"
            />
            <div className="flex items-center gap-2">
              {it.icon ? (
                <img
                  src={it.icon}
                  alt=""
                  className="h-9 w-9 rounded bg-[#0a1119] border border-[#27272A] object-contain p-1"
                  onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                />
              ) : (
                <div className="h-9 w-9 rounded bg-[#0a1119] border border-[#27272A] flex items-center justify-center text-zinc-600">
                  <ImageIcon size={14} />
                </div>
              )}
            </div>
            <input
              type="text"
              value={it.label}
              onChange={(e) => updateField(it.id, { label: e.target.value })}
              data-testid={`category-label-${it.slug}`}
              className="w-full h-8 px-2 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm font-semibold"
            />
            <div className="text-zinc-500 text-xs font-mono truncate" title={it.slug}>
              {it.slug}
            </div>
            <input
              type="text"
              value={it.to || ""}
              onChange={(e) => updateField(it.id, { to: e.target.value })}
              placeholder="/casino"
              data-testid={`category-to-${it.slug}`}
              className="w-full h-8 px-2 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm font-mono"
            />
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={() => toggleEnabled(it)}
                data-testid={`category-toggle-${it.slug}`}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold transition-colors ${
                  it.enabled
                    ? "text-emerald-400 bg-emerald-500/10 border border-emerald-500/30"
                    : "text-zinc-500 bg-zinc-500/10 border border-zinc-500/30"
                }`}
              >
                {it.enabled ? <Eye size={12} /> : <EyeOff size={12} />}
                {it.enabled ? "Visible" : "Hidden"}
              </button>
            </div>
            <div className="flex items-center gap-2 justify-center">
              <button
                type="button"
                onClick={() => {
                  // also allow editing icon url via prompt
                  const url = window.prompt("Icon URL (leave empty to clear):", it.icon || "");
                  if (url === null) return;
                  updateField(it.id, { icon: url });
                }}
                data-testid={`category-icon-${it.slug}`}
                className="px-2 py-1.5 text-xs font-semibold text-zinc-300 hover:text-white border border-[#27272A] hover:border-white/20 rounded-md transition-colors"
              >
                Icon
              </button>
              <button
                type="button"
                onClick={() => saveRow(it)}
                disabled={!it._dirty || saving[it.id]}
                data-testid={`category-save-${it.slug}`}
                className="px-2 py-1.5 text-xs font-semibold text-white rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                style={{ background: it._dirty ? ACCENT : "#27272A" }}
              >
                <Save size={12} /> Save
              </button>
              <button
                type="button"
                onClick={() => removeRow(it)}
                data-testid={`category-delete-${it.slug}`}
                className="p-1.5 text-zinc-500 hover:text-red-400 border border-[#27272A] hover:border-red-500/40 rounded-md transition-colors"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="text-[13px] text-zinc-500 leading-relaxed max-w-3xl">
        <p className="mb-1"><span className="text-zinc-300 font-semibold">Tip:</span> The <span className="font-mono text-zinc-300">Order</span> field controls display position (lower = earlier). Click <span className="font-mono text-zinc-300">Save</span> after editing. Changes go live on the site within a few seconds.</p>
        <p><span className="text-zinc-300 font-semibold">Route:</span> this is the path users land on when they click the category (e.g. <span className="font-mono">/casino</span>, <span className="font-mono">/sports</span>, or any custom URL). For external links, use full URLs with <span className="font-mono">http://</span>.</p>
      </div>

      {addOpen && (
        <AddCategoryModal
          defaultKind={tab}
          onClose={() => setAddOpen(false)}
          onCreated={(doc) => {
            setItems((prev) => [...prev, doc]);
            setAddOpen(false);
            toast.success(`Added "${doc.label}"`);
          }}
        />
      )}
    </div>
  );
}

function AddCategoryModal({ defaultKind, onClose, onCreated }) {
  const [form, setForm] = useState({
    slug: "",
    label: "",
    kind: defaultKind,
    to: "/",
    icon: "",
    order: 10,
    enabled: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.slug.trim() || !form.label.trim()) {
      toast.error("Slug and Label are required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/categories", form);
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="add-category-modal"
    >
      <div
        className="w-full max-w-lg rounded-xl p-6 space-y-4"
        style={{ background: PANEL_BG, border: `1px solid ${PANEL_BORDER}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Add Category</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Slug (unique id)" hint="e.g. e-sports, live-casino">
            <input
              value={form.slug}
              onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))}
              data-testid="add-category-slug"
              placeholder="e-sports"
              className="w-full h-10 px-3 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm"
            />
          </Field>
          <Field label="Label (shown to users)">
            <input
              value={form.label}
              onChange={(e) => set("label", e.target.value)}
              data-testid="add-category-label"
              placeholder="E-Sports"
              className="w-full h-10 px-3 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm"
            />
          </Field>
          <Field label="Kind">
            <select
              value={form.kind}
              onChange={(e) => set("kind", e.target.value)}
              data-testid="add-category-kind"
              className="w-full h-10 px-3 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm"
            >
              <option value="primary">Primary (Main nav)</option>
              <option value="secondary">Secondary (Quick links)</option>
            </select>
          </Field>
          <Field label="Order">
            <input
              type="number"
              value={form.order}
              onChange={(e) => set("order", Number(e.target.value))}
              data-testid="add-category-order"
              className="w-full h-10 px-3 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm"
            />
          </Field>
          <Field label="Route (URL path)" full>
            <input
              value={form.to}
              onChange={(e) => set("to", e.target.value)}
              data-testid="add-category-to"
              placeholder="/e-sports"
              className="w-full h-10 px-3 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm font-mono"
            />
          </Field>
          <Field label="Icon URL (optional)" full>
            <input
              value={form.icon}
              onChange={(e) => set("icon", e.target.value)}
              data-testid="add-category-icon"
              placeholder="https://..."
              className="w-full h-10 px-3 bg-[#0a1119] border border-[#27272A] rounded text-white text-sm"
            />
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            data-testid="add-category-cancel"
            className="px-4 py-2 text-sm text-zinc-400 hover:text-white border border-[#27272A] rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="add-category-submit"
            className="px-4 py-2 text-sm font-semibold text-white rounded-md disabled:opacity-60"
            style={{ background: ACCENT }}
          >
            {submitting ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, hint, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="text-[11px] text-zinc-600 mt-1">{hint}</p>}
    </div>
  );
}
